import {
  KNOWLEDGE_ACCEPTED_MIME_TYPES,
  KNOWLEDGE_SUPPORTED_EXTENSIONS,
} from "@frontend-shared/constants";
import { UploadCloudIcon } from "lucide-react";
import { memo, useCallback, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

interface IKnowledgeFileUploadProps {
  onUpload: (files: File[]) => void;
  isUploading: boolean;
}

// NOTE: The allow-list itself lives in frontend-shared so the mobile document
// picker filters on exactly the same MIME types (NSAI-4321). Includes legacy
// `application/msword`, which browsers report for `.doc` (NSAI-2796).
const ACCEPTED_FILE_TYPES = [...KNOWLEDGE_ACCEPTED_MIME_TYPES];

const ACCEPTED_FILE_TYPE_SET = new Set<string>(ACCEPTED_FILE_TYPES);

const SUPPORTED_EXTENSIONS_LABEL = KNOWLEDGE_SUPPORTED_EXTENSIONS.map(
  (extension) => extension.toUpperCase(),
).join(", ");

const KnowledgeFileUploadComponent = ({
  onUpload,
  isUploading,
}: IKnowledgeFileUploadProps): React.JSX.Element => {
  const { t } = useTranslation("knowledge");
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      setIsDragOver(true);
    },
    [],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      setIsDragOver(false);
    },
    [],
  );

  const processFiles = useCallback(
    (files: File[]): void => {
      const validFiles = files.filter((file) =>
        ACCEPTED_FILE_TYPE_SET.has(file.type),
      );

      if (validFiles.length < files.length) {
        toast.error(t("error.invalidFileType"));
      }

      if (validFiles.length > 0) {
        onUpload(validFiles);
      }
    },
    [onUpload, t],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      setIsDragOver(false);

      if (isUploading) return;

      processFiles(Array.from(event.dataTransfer.files));
    },
    [isUploading, processFiles],
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isUploading) return;

      processFiles(Array.from(event.target.files ?? []));
      // NOTE: Reset input so the same file can be re-selected
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    },
    [isUploading, processFiles],
  );

  return (
    <label
      htmlFor={isUploading ? undefined : inputId}
      data-testid="knowledge-file-upload-dropzone"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`block border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
        isUploading
          ? "border-border opacity-60 cursor-not-allowed"
          : isDragOver
            ? "border-primary bg-primary/5 cursor-pointer"
            : "border-border hover:border-primary/50 cursor-pointer"
      }`}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        className="sr-only"
        multiple
        disabled={isUploading}
        accept={ACCEPTED_FILE_TYPES.join(",")}
        onChange={handleFileSelect}
      />
      <UploadCloudIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
      <p className="text-sm font-medium mb-1">
        {isUploading ? t("uploading") : t("dropFilesHere")}
      </p>
      <p className="text-xs text-muted-foreground">
        {SUPPORTED_EXTENSIONS_LABEL}
      </p>
      {!isUploading && (
        <Button
          variant="outline"
          size="sm"
          className="mt-3 pointer-events-none"
          type="button"
          tabIndex={-1}
        >
          {t("browseFiles")}
        </Button>
      )}
    </label>
  );
};

export const KnowledgeFileUpload = memo(KnowledgeFileUploadComponent);
